/**
 * Lightweight Markdown Text Component
 *
 * Renders basic markdown formatting commonly used in AI-generated text:
 * - **bold text**
 * - _italics_ or *italics*
 * - Headers (## and ###)
 * - Numbered lists (1., 2., 3.)
 * - Unordered lists (* and -)
 * - Paragraphs
 */

import React from 'react';

interface MarkdownTextProps {
  text: string;
  className?: string;
  inline?: boolean;
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({ text, className = '', inline = false }) => {
  const renderInlineMarkdown = (text: string): React.ReactNode => {
    // Handle literal \n sequences often returned by LLMs
    const normalizedText = text.replace(/\\n/g, '\n');
    let parts: (string | React.ReactNode)[] = [normalizedText];

    // Parser for **bold** (greedy match across newlines)
    const parseBold = (input: (string | React.ReactNode)[]): (string | React.ReactNode)[] => {
      const result: (string | React.ReactNode)[] = [];
      const boldRegex = /\*\*([\s\S]+?)\*\*/;

      input.forEach(part => {
        if (typeof part !== 'string') {
          result.push(part);
          return;
        }

        let currentStr = part;
        let match;
        while ((match = boldRegex.exec(currentStr)) !== null) {
          if (match.index > 0) result.push(currentStr.slice(0, match.index));
          result.push(<strong key={Math.random()} className="font-bold text-slate-900 dark:text-white">{match[1]}</strong>);
          currentStr = currentStr.slice(match.index + match[0].length);
        }
        if (currentStr) result.push(currentStr);
      });
      return result;
    };

    // Parser for _italics_ or *italics*
    const parseItalic = (input: (string | React.ReactNode)[]): (string | React.ReactNode)[] => {
      const result: (string | React.ReactNode)[] = [];
      const italicRegex = /([*_])([\s\S]+?)\1/;

      input.forEach(part => {
        if (typeof part !== 'string') {
          result.push(part);
          return;
        }

        let currentStr = part;
        let match;
        while ((match = italicRegex.exec(currentStr)) !== null) {
          if (match.index > 0) result.push(currentStr.slice(0, match.index));
          result.push(<em key={Math.random()} className="italic opacity-90">{match[2]}</em>);
          currentStr = currentStr.slice(match.index + match[0].length);
        }
        if (currentStr) result.push(currentStr);
      });
      return result;
    };

    parts = parseBold(parts);
    parts = parseItalic(parts);

    return parts;
  };

  const renderMarkdown = (content: string) => {
    const lines = content.replace(/\\n/g, '\n').split('\n');
    const elements: React.ReactNode[] = [];
    let currentParagraph: string[] = [];
    let listItems: string[] = [];
    let inList = false;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join(' ').trim();
        if (paragraphText) {
          elements.push(
            <p key={`p-${elements.length}`} className="mb-4 last:mb-0">
              {renderInlineMarkdown(paragraphText)}
            </p>
          );
        }
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`ul-${elements.length}`} className="list-disc list-inside mb-4 space-y-2">
            {listItems.map((item, idx) => (
              <li key={idx} className="pl-2 marker:text-slate-400">
                {renderInlineMarkdown(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        flushList();
        flushParagraph();
        return;
      }

      // Headers
      if (trimmedLine.startsWith('## ')) {
        flushParagraph(); flushList();
        elements.push(<h2 key={`h2-${elements.length}`} className="text-xl font-bold mb-3 mt-4 first:mt-0">{renderInlineMarkdown(trimmedLine.slice(3))}</h2>);
        return;
      }
      if (trimmedLine.startsWith('### ')) {
        flushParagraph(); flushList();
        elements.push(<h3 key={`h3-${elements.length}`} className="text-lg font-semibold mb-2 mt-3 first:mt-0">{renderInlineMarkdown(trimmedLine.slice(4))}</h3>);
        return;
      }

      // Bullets
      const bulletMatch = trimmedLine.match(/^[*|-]\s+(.+)$/);
      if (bulletMatch) {
        if (!inList) flushParagraph();
        inList = true;
        listItems.push(bulletMatch[1]);
        return;
      }

      // Numbers
      const numListMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (numListMatch) {
        if (!inList) flushParagraph();
        inList = true;
        listItems.push(numListMatch[2]);
        return;
      }

      if (inList) flushList();
      currentParagraph.push(trimmedLine);
    });

    flushList();
    flushParagraph();
    return elements;
  };

  if (inline) {
    return <span className={className}>{renderInlineMarkdown(text)}</span>;
  }

  return (
    <div className={`prose prose-sm dark:prose-invert max-w-none ${className}`}>
      {renderMarkdown(text)}
    </div>
  );
};

export default MarkdownText;
